/**
 * Product Insights · LAE (Productivity, Schools mode): the page body. Three layout blocks, Key figures,
 * Trend and Schools, plus the School modal host.
 *
 * Ports LAE `features/productivity/SchoolsView.jsx`. Deliberate differences:
 *   - Blocks render through SEED EMERGE's `LayoutGrid` (page id `product-insights.lae`, defaults in
 *     `LAE_SCHOOLS_LAYOUT`); the page owns `usePageLayout` and passes the layout in. There is no
 *     non-layout fallback.
 *   - No rubric bands, no drill-downs (HeroChangesModal, useDrilldown), no counsellor modal.
 *   - The Split by assignment switch is remembered on the account with `useRememberedState`
 *     (`lae-schools-split`, default on); LAE's legacy localStorage key has nothing to migrate here.
 *     The table renders the control (C8 `onSplitChange`), only when the response has `groups`.
 *   - The trend metric and legend are remembered inside `TrendCard`; sort and column filters inside
 *     `SchoolsTable` (filter memory), not in the URL.
 *   - First load renders skeleton shapes per block instead of LAE's `DashboardSkeleton`.
 *   - The school modal gets the row's funnel (URL `schoolFunnel`, `''` = none); it falls back to the
 *     page funnel itself.
 */
import type { ReactNode } from 'react';
import { LayoutGrid } from '@/components/layout';
import { Button, Panel, PanelBody, PanelHeader } from '@/components/ui';
import { Skeleton } from '@/components/ui/Skeleton';
import { PiLoading } from '@/components/product-insights/pi-kit';
import { cx } from '@/lib/cx';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import type { LayoutBlockDefault, PageLayout } from '@/lib/use-page-layout';
import type { LaeProductivitySchoolsResponse } from '@/lib/types';
import { KpiStrip, KpiStripSkeleton } from './KpiStrip';
import { TrendCard } from './TrendCard';
import { SchoolsTable } from './SchoolsTable';
import { SchoolsExportButton } from './SchoolsExportButton';
import { SchoolModal } from './SchoolModal';
import { buildSchoolHero } from './lae-hero';
import { bucketTarget } from './lae-dates';
import { DEFAULT_PRESET } from './lae-params';
import type { LaeProductivityParams } from './lae-params';
import type { LaeSort } from './lae-school-rows';

/** Page layout defaults (`usePageLayout('product-insights.lae', LAE_SCHOOLS_LAYOUT)`). Never rename an id. */
// oxlint-disable-next-line react/only-export-components
export const LAE_SCHOOLS_LAYOUT: readonly LayoutBlockDefault[] = [
  { id: 'kpis', label: 'Key figures', width: 'full' },
  { id: 'trend', label: 'Trend', width: 'full' },
  { id: 'table', label: 'Schools', width: 'full' },
];

/** The hero has 17 tiles (spec 3.1); the first-load skeleton matches it. */
const HERO_TILE_COUNT = 17;
const DEFAULT_SORT: LaeSort = { key: 'calls', dir: 'desc' };
const TABLE_SCOPE = 'product-insights.lae.schools-v2';
const TABLE_MEMORY_ID = 'lae-schools';

const parseBool = (raw: unknown): boolean | undefined => (typeof raw === 'boolean' ? raw : undefined);

export interface SchoolsViewProps {
  /** The current period's list response; null/undefined before the first one arrives. */
  data: LaeProductivitySchoolsResponse | null | undefined;
  /** The previous equal-length period (totals and schools only); null while missing or failed. */
  prevData: LaeProductivitySchoolsResponse | null | undefined;
  /** A refetch is in flight while older data is shown. */
  refreshing: boolean;
  params: LaeProductivityParams;
  layout: PageLayout;
}

function TrendSkeleton() {
  return (
    <Panel>
      <PanelHeader title={<Skeleton className="h-3.5 w-32" />} actions={<Skeleton className="h-8 w-64 max-w-full" />} />
      <PanelBody>
        <div className="h-[240px] animate-pulse rounded-md bg-surface-sunken motion-reduce:animate-none" />
      </PanelBody>
    </Panel>
  );
}

export function SchoolsView({ data, prevData, refreshing, params, layout }: SchoolsViewProps) {
  const [split, setSplit] = useRememberedState<boolean>('lae-schools-split', true, parseBool);

  const loading = !data;
  const totals = data?.totals ?? null;
  const series = totals?.series ?? [];
  const schools = data?.schools ?? [];
  // Undefined when the response has no breakdown (an older API): the table falls back to schools.
  const groups = Array.isArray(data?.groups) ? data.groups : undefined;
  const groupCount = groups ? groups.length : schools.length;
  const granularity = data?.window.granularity ?? params.granularity;
  const buckets = data?.window.buckets ?? null;
  const funnelLabel = params.funnel
    ? (data?.funnels.find((f) => f.value === params.funnel)?.label ?? params.funnel)
    : null;

  const kpis = buildSchoolHero({
    totals,
    prev: prevData?.totals ?? null,
    series,
    schools,
    prevSchools: prevData ? prevData.schools : null,
    loading,
    granularity,
  });

  const filtersActive =
    !params.cycleIsDefault || params.funnel !== '' || params.source !== 'all' || params.preset !== DEFAULT_PRESET;

  const onBucketClick = (bucket: string) => {
    const target = bucketTarget(bucket, granularity, params.today);
    if (target) params.setPeriod(target);
  };

  const half = (id: string) => layout.widthOf(id) === 'half';

  const render: Record<string, () => ReactNode> = {
    kpis: () =>
      loading ? (
        <KpiStripSkeleton count={HERO_TILE_COUNT} half={half('kpis')} />
      ) : (
        <KpiStrip items={kpis} half={half('kpis')} />
      ),
    trend: () =>
      loading ? (
        <TrendSkeleton />
      ) : (
        <TrendCard
          series={series}
          buckets={buckets}
          granularity={granularity}
          onBucketClick={onBucketClick}
          height={240}
          memoryPrefix="lae"
          half={half('trend')}
        />
      ),
    table: () =>
      loading ? (
        <PiLoading variant="table" rows={10} />
      ) : (
        <SchoolsTable
          title="Schools"
          groups={groups}
          schools={schools}
          totals={totals ?? undefined}
          funnel={params.funnel}
          split={split}
          onSplitChange={groups ? setSplit : undefined}
          scope={TABLE_SCOPE}
          memoryId={TABLE_MEMORY_ID}
          defaultSort={DEFAULT_SORT}
          onOpen={(schoolId, funnel) => params.setOpenSchool(schoolId, funnel)}
          actions={(visibleRows) => (
            <SchoolsExportButton
              rows={visibleRows}
              totals={totals}
              groupCount={groupCount}
              split={split}
              params={params}
              funnelLabel={funnelLabel}
            />
          )}
          footer
          emptyAction={
            filtersActive ? (
              <Button variant="secondary" size="sm" className="max-sm:h-11" onClick={params.resetFilters}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ),
  };

  return (
    <>
      <div
        className={cx(
          'transition-opacity motion-reduce:transition-none',
          refreshing && !loading && 'opacity-70',
        )}
      >
        <LayoutGrid layout={layout} render={render} />
      </div>

      {params.openSchool ? (
        <SchoolModal
          schoolId={params.openSchool}
          funnel={params.openSchoolFunnel}
          params={params}
          listData={data ?? null}
          onClose={() => params.setOpenSchool(null)}
        />
      ) : null}
    </>
  );
}
